/**
 * Role reads for the forwards pages. The pages live under `/admin` (staff-only, user decision,
 * 2026-10-12), whose guard admits the account; what a page then lets the session do is read off
 * these.
 *
 * Presentation only. Every forwards endpoint enforces the same ladder itself, so these change what
 * is offered, never what is permitted.
 */

import { reachesAccessClass } from "@/composables/useAdminCheck";
import type { Account } from "@/types";

/**
 * Whether this session is forwards staff: may cut a deal, renominate a contract, set producer
 * prices, move escrow.
 *
 * Super admins only. A forward sale is a liability the platform takes on, and a `sales_admin` does
 * not carry the authority to take that risk (user decision, 2026-09-17). It reads every account's
 * forwards (2026-09-23, reads only), and the pages it opens take what may be changed from the
 * capabilities on each response rather than from a role. Deliberately not `isAdminAccount`, which
 * admits both admin roles and every admin-driven session, and which the general admin surfaces (the
 * sales transaction behind a delivery) still go by.
 *
 * Judged on the *effective* role, so a super admin managing a client keeps it — the same account
 * the backend's admin routes read.
 */
export function isForwardsStaff(account: Account | null | undefined): boolean {
  return reachesAccessClass(account, "SUPER");
}

/**
 * Whether this session reads forwards as staff: what the admin forwards reads' `SALES_READ` admits,
 * forwards staff or a sales admin.
 *
 * A sales admin opens the volume picker and the pay-as-produced wizard read-only and is offered the
 * links to them (user decision, 2026-09-28), as it reads every account's forwards (2026-09-23).
 * What those pages let it do is still `isForwardsStaff`'s answer — creating a deal stays a super
 * admin's. Judged on the effective role like `isForwardsStaff`, so a sales admin managing a client
 * keeps it: the admin routes behind both pages read the performing account.
 */
export function readsForwardsAsStaff(
  account: Account | null | undefined
): boolean {
  return reachesAccessClass(account, "SALES_READ");
}
